/**
 * Backward-compatibility shims for the swiper system.
 *
 * Three globals are kept stable for foreign callers (other JS modules and
 * inline section scripts that expect them to exist):
 *
 *   window.sectionSwiperRegistry     — Map<string, Swiper>
 *   window.destroySwipersInSection   — (HTMLElement | string) => void
 *   window.reinitSwipers             — (HTMLElement | Document) => void
 *
 * Internal swiper code MUST NOT import this file. It uses core/registry.js
 * directly. Anything in here exists to bridge the legacy API surface to the
 * new registry without churning every consumer in one go.
 *
 * Known consumers of these globals at the time of writing:
 *   - src/js/app/bundle-product.js
 *   - sections/bundle-items.liquid (inline script)
 *   - sections/featured-collection.liquid (inline script)
 */

import registry from '../core/registry.js';

/**
 * Proxy that exposes `registry.byId` as a Map-shaped object. We don't expose
 * the actual Map because external code occasionally calls .set() on it, and
 * we want all writes to go through the registry's accessors so internal
 * invariants stay enforceable in the future.
 */
class SectionSwiperRegistryShim {
  get size() {
    return registry.byId.size;
  }

  get(id) {
    return registry.byId.get(id);
  }

  set(id, swiper) {
    registry.registerById(id, swiper);
    return this;
  }

  has(id) {
    return registry.byId.has(id);
  }

  delete(id) {
    return registry.byId.delete(id);
  }

  forEach(cb, thisArg) {
    registry.byId.forEach(cb, thisArg);
  }

  clear() {
    registry.byId.clear();
  }

  keys() {
    return registry.byId.keys();
  }

  values() {
    return registry.byId.values();
  }

  entries() {
    return registry.byId.entries();
  }

  [Symbol.iterator]() {
    return registry.byId[Symbol.iterator]();
  }
}

/**
 * Destroy swipers reachable from `target`.
 *  - If `target` is a string, it's treated as a section id and we tear down
 *    the matching entry in `byId`.
 *  - If `target` is a `.swiper-section` element (or contains one), we tear
 *    down the section's full swiper list via the registry.
 */
function destroySwipersInSection(target) {
  if (typeof target === 'string') {
    const swiper = registry.byId.get(target);
    if (swiper && typeof swiper.destroy === 'function') {
      try {
        swiper.destroy(true, true);
      } catch (err) {
        console.warn('Failed to destroy swiper:', err);
      }
    }
    registry.byId.delete(target);
    return;
  }

  if (target instanceof Element) {
    const section = target.classList.contains('swiper-section')
      ? target
      : target.querySelector('.swiper-section');
    if (section) {
      registry.deleteSection(section);
    }
    return;
  }
}

/**
 * Install all back-compat globals.
 *
 * Must be called by `index.js` after the registry has been imported and
 * before any other module reads the globals.
 *
 * @param {Object} deps
 * @param {Array} deps.plugins        Plugin list to use when reinitializing.
 * @param {Function} deps.Initializer Initializer class (passed in to avoid a
 *                                    circular import with core/initializer.js).
 */
export function installBackCompat({ plugins, Initializer }) {
  if (typeof window === 'undefined') return;

  // Replace any pre-existing Map on window. Older code may have already done
  // `window.sectionSwiperRegistry = window.sectionSwiperRegistry || new Map()`,
  // so we copy any existing entries into our shim before swapping.
  const existing = window.sectionSwiperRegistry;
  const shim = new SectionSwiperRegistryShim();
  if (existing && typeof existing.forEach === 'function' && existing !== shim) {
    existing.forEach((value, key) => shim.set(key, value));
  }
  window.sectionSwiperRegistry = shim;

  window.destroySwipersInSection = destroySwipersInSection;

  window.reinitSwipers = function reinitSwipers(target = document) {
    if (target instanceof Element) {
      destroySwipersInSection(target);
    } else {
      const sectionsToTeardown = [];
      registry.forEachSection((_, section) => sectionsToTeardown.push(section));
      sectionsToTeardown.forEach((section) => registry.deleteSection(section));
    }

    // Defer to the singleton initializer rather than spinning up a new one.
    //
    // Two reasons:
    //   1. Each `new Initializer().init()` binds another window resize
    //      listener; we'd leak one per `reinitSwipers()` call. The singleton's
    //      `refresh()` reuses the existing listener.
    //   2. `new Initializer({ root: target }).init()` calls
    //      `target.querySelectorAll('.swiper-section[...]')`, which doesn't
    //      match `target` itself. `<bundle-gallery>` IS a `.swiper-section`,
    //      so scoping by it would silently miss the only section worth
    //      reinitializing. `refresh()` always scans `document`, so the
    //      target-shape distinction stops mattering.
    //
    // `window.swiperInitializer` is the canonical name; the underscored
    // alias is a one-release deprecation cushion. Read either, prefer the
    // canonical, fall back to the alias.
    const initializer =
      window.swiperInitializer || window.__swiperInitializer;
    if (initializer && typeof initializer.refresh === 'function') {
      initializer.refresh();
    } else {
      // Bootstrap hasn't completed yet (e.g. caller fired before
      // DOMContentLoaded). Fall back to a fresh init scoped to document so
      // every section gets a chance, accepting the one-time resize listener
      // duplicate as the lesser evil.
      new Initializer({ plugins }).init();
    }
  };
}
